import { format } from 'date-fns';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { ToastMessage } from 'modules/dragAndDropEditor/components';
import { UPDATE_SAVED_ROW_ERROR_MESSAGE } from 'modules/dragAndDropEditor/constants';
import { useUpdateSavedRowMutation } from 'modules/dragAndDropEditor/hooks/useUpdateSavedRowMutation';
import {
  GetContentCallback,
  SavedRow,
  UpdateSavedRowResponse,
} from 'modules/dragAndDropEditor/types';
import {
  createSavedRowFailureToErrorMessage,
  getUpdateSaveRowDesignVariables,
} from 'modules/dragAndDropEditor/utils';
import {
  addNegativeToastWithErrorId,
  addPositiveToast,
  removeToastKey,
} from 'modules/toastContainer';
import { body2, colors, fontWeights, link2, spacing } from 'ui/theme';
import { parseDate } from 'utils/datetime/date';
import { generateId } from 'utils/helpers';
import { useEditableFieldsContext } from '../context/EditableFieldsContext';
import { useEditSavedRowDesignContext } from '../context/EditSavedRowDesignContext';

const ReturnToTemplateLink = styled.span`
  ${body2};
  ${link2}
  display: inline-block;
  margin-top: ${spacing.half};
  color: ${colors.textOnColor};
  text-decoration-color: ${colors.textOnColor};
  text-decoration-thickness: ${spacing.single};
  && > #edit-saved-row-template {
    display: inline;
    font-weight: ${fontWeights.semiBold};
  }
`;

const savedRowSuccessToastId = generateId();

export const useUpdateSavedRowDesign = () => {
  const { savedRow, pageBeeBuilderMetadata, updateEditSavedRowDesign, onExitSavedRowDesignEditor } =
    useEditSavedRowDesignContext();
  const { liveTemplate, getDragAndDropContentRef } = useEditableFieldsContext();

  const dispatch = useDispatch();

  const { isUpdatingSavedRow, updateSavedRow, ...rest } = useUpdateSavedRowMutation();

  /**
   * Saves the currently editing row design
   * @param onSuccess Callback function to be called after the row is saved successfully
   * @param showReturnToTemplate Whether to show the return to template link
   */
  const onSaveRowDesign = ({
    onSuccess,
    showReturnToTemplate = true,
  }: {
    onSuccess?: () => void;
    showReturnToTemplate?: boolean;
  } = {}) => {
    getDragAndDropContentRef?.current?.(((args) => {
      const beeBuilderHTML = args?.html;

      const variables = getUpdateSaveRowDesignVariables(
        savedRow as SavedRow,
        beeBuilderHTML,
        pageBeeBuilderMetadata as string,
      );

      updateSavedRow({
        variables,
        onCompleted: (data: UpdateSavedRowResponse) => {
          const _failure = data?.updateBeeRow?.failure;

          if (_failure) {
            const message = createSavedRowFailureToErrorMessage(_failure);
            dispatch(addNegativeToastWithErrorId(message, 'useUpdateSavedRowDesign', data));
          } else {
            dispatch(
              addPositiveToast(
                <ToastMessage
                  name={data?.updateBeeRow?.beeRowName ?? ''}
                  message={showReturnToTemplate ? 'saved.' : 'updated successfully.'}
                >
                  {showReturnToTemplate && (
                    <ReturnToTemplateLink
                      tabIndex={0}
                      role="button"
                      onClick={() => {
                        onExitSavedRowDesignEditor();
                        dispatch(removeToastKey(savedRowSuccessToastId)); // closes the toast
                      }}
                    >
                      Return to&nbsp;
                      <p id="edit-saved-row-template">{liveTemplate?.name}</p>
                    </ReturnToTemplateLink>
                  )}
                </ToastMessage>,
                {
                  key: savedRowSuccessToastId,
                },
              ),
            );

            /**
             * NOTE: sets the updatedAt to the current date until we are able to get the server data.
             * This is a (hopefully) temporary solution until we can fetch individual rows from the server.
             */
            const updatedAt = format(parseDate(), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx");

            /**
             * Update the saved row being edited data with the new content, and update the updatedAt field
             */
            const updatedRow = {
              ...savedRow,
              updatedAt,
              primaryContent: {
                ...savedRow?.primaryContent,
                html: variables.content?.html,
                beeBuilderMetadata: variables.content?.beeBuilderMetadata,
              },
            };

            updateEditSavedRowDesign(updatedRow as SavedRow);
            onSuccess?.();
          }
        },
        onError: (error: any) => {
          dispatch(
            addNegativeToastWithErrorId(
              UPDATE_SAVED_ROW_ERROR_MESSAGE,
              'useUpdateSavedRowDesign',
              error,
            ),
          );
        },
      });
    }) as GetContentCallback);
  };

  return { ...rest, isUpdatingSavedRow, updateSavedRow, onSaveRowDesign };
};
